import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  CardContent,
  CardActions,
  Grid,
  Card,
  Divider,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { getVenues, getNotifications, sendBulkNotification, deleteNotifications } from "../api";
import Loader from "../components/common/Loader";
import NotificationModal from "../components/NotificationModal";
import NotificationRecipientsModal from "../components/NotificationRecipientsModal";
import DeleteModal from "../components/DeleteModal";

const getRecipientText = (item) => {
  const previewUsers = item.recipientPreviewUsers || [];
  const previewNames = previewUsers
    .map((user) => user.username || user.email || user.id)
    .filter(Boolean);

  const restCount = Math.max((item.recipientUsersCount || 0) - previewNames.length, 0);
  if (!previewNames.length && !restCount) return "-";

  return restCount
    ? `${previewNames.join(", ")} +${restCount}`
    : previewNames.join(", ");
};

const formatEvent = (event) => {
  const date = event.date
    ? new Date(event.date).toLocaleDateString([], {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;
  const parts = [event.name, date && `(${date})`, event.venueName && `@ ${event.venueName}`];
  return parts.filter(Boolean).join(" ");
};

export default function NotificationsPage() {
  const [state, setState] = useState({
    data: undefined,
    open: false,
    venueOptions: [],
    selectedNotification: null,
  });

  useEffect(() => {
    (async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) return;

      if (user.role !== "admin") {
        setState((s) => ({
          ...s,
          data: [],
          role: user.role,
          user,
        }));
        return;
      }

      const [data, venues] = await Promise.all([
        getNotifications(),
        getVenues(user.country),
      ]);

      const venueOptions = (venues || [])
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .map((venue) => ({ id: venue.id, label: venue.name || venue.id }));

      setState((s) => ({
        ...s,
        data: data || [],
        venueOptions,
        role: user.role,
        user,
      }));
    })();
  }, []);

  const handleCreate = () => {
    setState((s) => ({
      ...s,
      open: true,
      handleConfirm: async (body) => {
        const report = await sendBulkNotification({
          notificationTitle: body.title,
          notificationBody: body.body,
          venuesIds: body.venueIds || [],
          selectedEventIds: [],
        });

        const data = await getNotifications();
        setState((prevState) => ({
          ...prevState,
          data,
          sendReport: report,
        }));
      },
    }));
  };

  const handleClose = () => {
    setState((s) => ({
      ...s,
      open: false,
      handleConfirm: undefined,
    }));
  };

  const handleDelete = (item) => () => {
    setState((s) => ({
      ...s,
      deleteOpen: true,
      deleteName: item.title || "Untitled Notification",
      handleDeleteConfirm: async () => {
        await deleteNotifications({ createdAt: item.createdAt, title: item.title, body: item.body });
        setState((prev) => ({
          ...prev,
          deleteOpen: false,
          data: prev.data.filter((x) => x.id !== item.id),
        }));
      },
    }));
  };

  const handleDeleteClose = () => {
    setState((s) => ({ ...s, deleteOpen: false, deleteName: undefined, handleDeleteConfirm: undefined }));
  };

  return (
    <>
      <Grid
        container
        spacing={3}
        sx={{ maxWidth: "1600px", paddingTop: "80px", paddingX: 4 }}
        justifyContent="center"
      >
        {state.role === "admin" && (
          <Grid item xs={12}>
            <Button type="button" onClick={handleCreate} variant="contained">
              Create Notification
            </Button>
          </Grid>
        )}
        {state.sendReport && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ backgroundColor: "#3a3349", color: "white", fontFamily: "monospace", whiteSpace: "pre-wrap", padding: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="secondary" gutterBottom>
                  Send Report
                </Typography>
                <Typography variant="body2" component="pre" sx={{ color: "#d8c8f4", fontFamily: "monospace", fontSize: "0.8rem" }}>
                  {[
                    `Venues targeted:     ${state.sendReport.venueIds?.length ?? "-"}`,
                    `Events matched:      ${state.sendReport.eventsMatched ?? "-"}`,
                    `Analytics items:     ${state.sendReport.analyticsItems ?? "-"}`,
                    `Unique users:        ${state.sendReport.uniqueUsers ?? "-"}`,
                    `Resolved users:      ${state.sendReport.resolvedUsers ?? "-"}`,
                    `Skipped (today):     ${state.sendReport.skippedDailyLimit ?? "-"}`,
                    `Push eligible:       ${state.sendReport.pushEligibleUsers ?? "-"}`,
                    `Stored:              ${state.sendReport.stored ?? "-"}`,
                    `Push sent:           ${state.sendReport.sent ?? "-"}`,
                    `Failed:              ${state.sendReport.failed ?? "-"}`,
                    `Opened:              ${state.sendReport.clickedCount ?? 0} (clickedAt tracked per user on tap)`,
                    ...(state.sendReport.events?.length
                      ? ["", "Events:", ...state.sendReport.events.map((e) => `  - ${e.name || "(unnamed)"} (${e.date || "no date"}) @ ${e.venueName || "unknown venue"}`)]
                      : []),
                  ].join("\n")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {state.data ? (
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{ textAlign: "left", color: "white", marginBottom: "10px" }}
            >
              Notifications
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              {state.data.map((item) => (
                <Grid item xs={12} key={item.id} sx={{ display: "flex", justifyContent: "center" }}>
                  <Card
                    variant="outlined"
                    sx={{
                      width: { xs: "100%", md: "70vw", lg: "65vw" },
                      maxWidth: "1100px",
                      backgroundColor: "#4b445b",
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" color="secondary" gutterBottom>
                        {item.title || "Untitled Notification"}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="secondary"
                        sx={{ whiteSpace: "pre-wrap", marginBottom: 2 }}
                      >
                        {item.body || "-"}
                      </Typography>
                      <Divider sx={{ borderColor: "rgba(216, 200, 244, 0.2)", marginBottom: 1.5 }} />
                      <Typography
                        variant="body2"
                        color="secondary"
                        onClick={() => setState((s) => ({ ...s, selectedNotification: item }))}
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        Sent to: {getRecipientText(item)}
                      </Typography>
                      <Typography variant="body2" color="secondary">
                        Opened: {item.clickedCount ?? 0} / {item.recipientUsersCount ?? 0}
                      </Typography>
                      <Typography variant="body2" color="secondary">
                        Sent at: {item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}
                      </Typography>
                      {item.selectedEvents?.length > 0 && (
                        <Typography variant="body2" color="secondary">
                          Targeted event(s): {item.selectedEvents.map(formatEvent).join(", ")}
                        </Typography>
                      )}
                      {item.targetEvents?.length > 0 && (
                        <Typography variant="body2" color="secondary">
                          Recipients qualified via: {item.targetEvents.map((e) => formatEvent({ name: e.name, date: e.date })).join(", ")}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: "flex-end" }}>
                      <IconButton onClick={handleDelete(item)} sx={{ color: "#d8c8f4" }}>
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        ) : (
          <Loader />
        )}
      </Grid>
      {state.selectedNotification && (
        <NotificationRecipientsModal
          notificationId={state.selectedNotification.id}
          recipientCount={state.selectedNotification.recipientUsersCount}
          onClose={() => setState((s) => ({ ...s, selectedNotification: null }))}
        />
      )}
      {state.open && (
        <NotificationModal
          user={state.user}
          handleConfirm={state.handleConfirm}
          handleClose={handleClose}
          showVenuesField={true}
          venueOptions={state.venueOptions}
        />
      )}
      {state.deleteOpen && (
        <DeleteModal
          name={state.deleteName}
          handleConfirm={state.handleDeleteConfirm}
          handleClose={handleDeleteClose}
        />
      )}
    </>
  );
}
